import { Component, HostListener, OnDestroy, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MessagingService } from './services/messaging.service';
import { CommonService } from './services/common.service';
import { environment } from '../environments/environment';
import { Title , Meta} from '@angular/platform-browser';
import { MaterialModule } from './variables/material-imports';
import { FaviconService } from './services/favicon.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ManifestService } from './manifest.service';
import { SwUpdate } from '@angular/service-worker';
import { Subscription, filter } from 'rxjs';
import { DynamicLinkDailogComponent } from './pages/home/dynamic-link-dailog/dynamic-link-dailog.component';
import { CheckoutModule } from 'paytm-blink-checkout-angular';
import { ExternalScriptLoaderService } from './services/external-script-loader.service';
import { SessionService, clearLocalStorageExcept } from './services/session.service';
import { InstallPromptService } from './services/install-prompt.service';

export { clearLocalStorageExcept } from './services/session.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, MaterialModule, ServiceWorkerModule, CheckoutModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: []
})
export class AppComponent implements OnDestroy {
  screenLoading : boolean = true;
  errorMessage:string | null = null
  businessDisabled: boolean = false; // Add this flag for business disabled state
  showPaymentWait = window.location.pathname.includes('payment-success');
  paymentThemeColor = localStorage.getItem('primaryColor') || '#EB1F27';
  private paymentReadySub?: Subscription;
  private routerSub?: Subscription;
  private sessionCheckInterval?: ReturnType<typeof setInterval>;
  
  constructor(
    private readonly messagingService: MessagingService,
    private commonService: CommonService,
    private titleService: Title,
    private metaService: Meta,    
    private favService: FaviconService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar : MatSnackBar,
    private manifestService:ManifestService,
    private swUpdate: SwUpdate,
    private renderer: Renderer2,
    private externalScripts: ExternalScriptLoaderService,
    private session: SessionService,
    private installPrompt: InstallPromptService,
  ) {
    // Restore brand theme immediately so refreshed tab pages don't fall back to default CSS color.
    this.applyThemeColor(localStorage.getItem('primaryColor') || environment.primaryColor || '#EB1F27');
    const cachedName = localStorage.getItem('name') || localStorage.getItem('bName') || '';
    const cachedLogo = localStorage.getItem('downloadLogo') || '';
    if (cachedName && cachedLogo) {
      this.manifestService.updateManifest(cachedName, cachedName, cachedLogo);
    }
    this.applyIosPwaLayoutFix();
    this.setupPaymentWaitListener();
    if (this.showPaymentWait) {
      this.screenLoading = false;
      this.listenForPaymentReady();
    }
    // Gift-voucher links from WhatsApp/etc. must leave the in-app browser so the installed PWA can open.
    if (this.installPrompt.tryHandoffGiftVoucherToInstalledApp()) {
      return;
    }
    // White-label: start theme API first — do not wait on FCM / third-party SDKs.
    this.getThemeColors();
    queueMicrotask(() => this.messagingService.receiveMessage());
  }

  /** iOS Home Screen only — pins layout flush; never applied on PC demos. */
  private applyIosPwaLayoutFix(): void {
    if (this.messagingService.isIosDevice() && this.messagingService.isStandalonePwa()) {
      document.documentElement.classList.add('ios-pwa');
    }
  }

  /**
   * Cashfree/PhonePe do a full redirect → AppComponent remounts with showPaymentWait=true.
   * Paytm uses router.navigate (SPA) → must flip the loader on NavigationEnd.
   */
  private setupPaymentWaitListener(): void {
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const onPaymentSuccess = event.urlAfterRedirects.includes('/payment-success');
        if (onPaymentSuccess) {
          this.showPaymentWait = true;
          this.screenLoading = false;
          this.paymentThemeColor = localStorage.getItem('primaryColor') || '#EB1F27';
          this.listenForPaymentReady();
        } else if (this.showPaymentWait) {
          this.showPaymentWait = false;
        }
      });
  }

  private listenForPaymentReady(): void {
    this.paymentReadySub?.unsubscribe();
    this.paymentReadySub = this.commonService.paymentReady$.subscribe(() => {
      this.showPaymentWait = false;
    });
  }

  ngOnDestroy(): void {
    this.paymentReadySub?.unsubscribe();
    this.routerSub?.unsubscribe();
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
  }

  lightenColor(hex: string, percent: number): string {
    const color = hex?.startsWith('#') ? hex : '#EB1F27';
    const num = parseInt(color.replace('#', ''), 16);
    const r = num >> 16;
    const g = (num >> 8) & 0x00ff;
    const b = num & 0x0000ff;
    return `rgb(${Math.round(r + (255 - r) * (percent / 100))}, ${Math.round(g + (255 - g) * (percent / 100))}, ${Math.round(b + (255 - b) * (percent / 100))})`;
  }

  ngOnInit(): void {
    this.startSessionWatch();

    const pathname = window.location.pathname;
    if (pathname.includes('payment-success')) {
      this.router.navigateByUrl(pathname + window.location.search);
    }

    // Enforce 15-min idle logout even after app/tab was closed and reopened
    this.checkSessionExpiryOnResume();

    // PWA caching SW (production). FCM uses a separate narrow-scope SW via MessagingService.
    if ('serviceWorker' in navigator && environment.production) {
      navigator.serviceWorker.register('ngsw-worker.js')
        .then(registration => {
          console.log('Service Worker registered:', registration);
        })
        .catch(err => {
          console.error('Service Worker registration failed:', err);
        });
    }

    if (environment.production && this.swUpdate.isEnabled) {
      this.handleServiceWorkerUpdates();
    }
  }
  
  private handleServiceWorkerUpdates(): void {
    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        if (confirm('A new version of the app is available. Reload to update?')) {
          window.location.reload();
        }
      }
    });

    setInterval(() => {
      this.swUpdate.checkForUpdate();
    }, 60 * 60 * 1000);
  }

  // updateManifestBasedOnUrl(): void {
  //   this.commonService.getThemes(this.data).subscribe((res: any) => {
  //     // Check for null response
  //     if (res === null || res.data === null) {
  //       this.screenLoading = false;
  //       this.businessDisabled = true;
  //       return;
  //     }
      
  //     this.manifestService.updateManifest(res.data.name, res.data.name, res.data.logo);
  //     this.updateIosMetaTags(res.data);
  //     this.updateMetaTagsData(res);
  //     this.titleService.setTitle(res.data.name);
  //     this.updateIosAppTitle(res.data.name); 
  //   }); 
  // }

  private updateIosMetaTags(data: any) {
    const sizes = ['72x72', '96x96', '128x128', '144x144', '152x152', '167x167', '180x180', '192x192', '384x384', '512x512'];
  
    const existingIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
    existingIcons.forEach(icon => icon.remove());
  
    sizes.forEach(size => {
      const link: HTMLLinkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'apple-touch-icon');
      this.renderer.setAttribute(link, 'sizes', `${size}`);
      this.renderer.setAttribute(link, 'href', data.logo);
      this.renderer.appendChild(document.head, link);
    });
  }

  private updateIosAppTitle(appName: string) {
    let metaTag = document.querySelector('meta[name="apple-mobile-web-app-title"]') as HTMLMetaElement;
  
    if (!metaTag) {
      metaTag = this.renderer.createElement('meta');
      this.renderer.setAttribute(metaTag, 'name', 'apple-mobile-web-app-title');
      this.renderer.appendChild(document.head, metaTag);
    }
  
    this.renderer.setAttribute(metaTag, 'content', appName);
  }

  data: any = {
    url: ""
  }
  themeData: any;

  getThemeColors() {
    if (window.location.origin.includes('localhost') || environment.flavour == "local") {
      this.data.url = 'https://jewellers.dev.passbookapp.in/';
    } else {
      if(window.localStorage.getItem("url") == "undefined" || !window.localStorage.getItem("url")){
        window.localStorage.setItem("url", window.location.href);
      }
      this.data.url = window.localStorage.getItem("url");
    }
    
    const url = new URL(this.data.url);
    const queryParams = new URLSearchParams(url.search);
    const liveParams = new URLSearchParams(window.location.search);

    const voucherCode = (liveParams.get('voucherCode') || queryParams.get('voucherCode') || '').trim();
    const campaignId = (liveParams.get('campaignId') || queryParams.get('campaignId') || '').trim();
    if (voucherCode && campaignId) {
      localStorage.setItem('giftVoucherCode', voucherCode);
      localStorage.setItem('giftCampaignId', campaignId);
    }

    // ✅ Extract all needed params safely
    const redirectTo = queryParams.get('redirectTo');
    const sId = queryParams.get('sId');
    const passbookId = queryParams.get('passbookId');
    const transactionId = queryParams.get('transactionId');

    // ✅ Store UTM parameters FIRST (only when present — never overwrite with empty string)
    if (queryParams.get('utm_u_id')) localStorage.setItem('utm_u_id', queryParams.get('utm_u_id')!);
    if (queryParams.get('utm_ref_id')) localStorage.setItem('utm_ref_id', queryParams.get('utm_ref_id')!);
    if (queryParams.get('utm_ref_code')) localStorage.setItem('utm_ref_code', queryParams.get('utm_ref_code')!);
    if (queryParams.get('utm_b_id')) localStorage.setItem('utm_b_id', queryParams.get('utm_b_id')!);
    
    if (queryParams.get('utm_ref_id')) {
      this.linkClick();
    }
    
    if((localStorage.getItem("NavigetedThroughLink")!=="True") ){
      if(redirectTo !== null) localStorage.setItem("redirectTo", redirectTo);
      if(sId !== null) localStorage.setItem("repId", sId);
      if(passbookId !== null) localStorage.setItem("passbookId", passbookId);
      if(transactionId !== null) localStorage.setItem("transactionId", transactionId);
    }
    
    const isLoggedIn = localStorage.getItem('token') !== null;

    // Handle redirectTo cases
    if (redirectTo !== null && redirectTo === 'edit_profile') {
      if (isLoggedIn) {
        localStorage.setItem("NavigetedThroughLink","True");
        this.router.navigate(['/profile/edit-profile']);
      } else {
        const sId = localStorage.getItem('repId');
        const redirectTo = localStorage.getItem('redirectTo');
        clearLocalStorageExcept(['sId', 'bId', 'repId', 'redirectTo','storeList','merchants','countryList']);
        this.router.navigate([''], { queryParams: { sId: localStorage.getItem('repId'), redirectTo: localStorage.getItem('redirectTo') } });
      }
    } 
    else if (redirectTo !== null && redirectTo === 'due_payments') {
      if (isLoggedIn && redirectTo === 'due_payments') {
        localStorage.setItem('NavigetedThroughLink', 'True');
        this.router.navigate(['/home']);
      } else {
        clearLocalStorageExcept(['sId', 'bId', 'repId', 'redirectTo', 'passbookId', 'transactionId','storeList','merchants','countryList']);
        this.router.navigate([''], { queryParams: { sId: localStorage.getItem('repId'), redirectTo: localStorage.getItem('redirectTo'), passbookId:localStorage.getItem('passbookId'), transactionId:localStorage.getItem('transactionId') } });
      }
    } 
    else {
      // ✅ REMOVED the broken /?sId= block entirely.
      // No extra extraction – we already stored repId from queryParams above.
      // The old block used to overwrite repId incorrectly – deleted.
    }

    this.commonService.getThemes(this.data).subscribe({
      next: (res: any) => {
        // Check if response is null (business is disabled)
        if (res === null || res.data === null) {
          this.screenLoading = false;
          this.businessDisabled = true;
          return;
        }

        this.manifestService.updateManifest(res.data.name, res.data.name, res.data.logo);
        this.updateIosMetaTags(res.data);
        this.updateMetaTagsData(res);
        this.titleService.setTitle(res.data.name);
        this.updateIosAppTitle(res.data.name);
       
        const faviconUrl = res.data.favIcon || 'assets/default-favicon.png';
        this.updateFavicon(faviconUrl); 
        this.metaService.updateTag({ name: 'description', content: res.data.description || 'Default description' });
        this.metaService.updateTag({ name: 'keywords', content: res.data.keywords || 'Default keywords' });
    
        const logoUrl = res.data.logo || 'assets/default-logo.png';
        this.metaService.updateTag({ property: 'og:image', content: logoUrl });
    
        this.favService.updateFavicon(res.data.favIcon);
        this.themeData = res.data;
        
        localStorage.setItem('bId', res.data.id);
        localStorage.setItem('bName', res.data.name);
        // localStorage.setItem('sId', res.data.storeId);
        const stores = Array.isArray(res.data.storeList) ? res.data.storeList : [];
        localStorage.setItem('storeList', JSON.stringify(stores));

      if (stores.length === 1) {
        // Single store → auto select
        localStorage.setItem('sId', stores[0].id);
        localStorage.setItem('storeName', stores[0].storeName);
      } else {
        // Multiple stores → user must select
        localStorage.removeItem('sId');
        localStorage.removeItem('storeName');
      }
        localStorage.setItem('name', res.data.name);
        localStorage.setItem('entityName', res.data.entityName);
        localStorage.setItem('googleLink', res.data.googleLink);
        localStorage.setItem('primaryColor', res.data.primaryColor);
        localStorage.setItem('downloadLogo', this.themeData.logo);
        this.messagingService.setBrandLogo(this.themeData.logo);
        localStorage.setItem('paymentType', res.data.paymentType);
        localStorage.setItem('status', res.data.status);
        localStorage.setItem('referralEnabled', res.data.business?.referralEnabled);
        localStorage.setItem('voucherEnabled', res.data.business?.voucherEnabled);
        localStorage.setItem(
          'paymentEnabled',
          JSON.stringify(res.data.business?.paymentEnabled === true)
        );
        localStorage.setItem(
          'nomineeRequiredForEnrollment',
          JSON.stringify(res.data.business?.nomineeRequiredForEnrollment === true)
        );
        localStorage.setItem(
          'ratingEnable',
          JSON.stringify(res.data.business?.ratingEnable === true)
        );
        localStorage.setItem(
          'enableHomeStoreSelection',
          JSON.stringify(res.data.business?.enableHomeStoreSelection ?? true)
        );
        localStorage.setItem(
          'enableStoreStaffSelection',
          JSON.stringify(res.data.business?.enableStoreStaffSelection ?? true)
        );
        localStorage.setItem(
          'showStaff',
          JSON.stringify(res.data.business?.showStaff === true || res.data.showStaff === true)
        );
        localStorage.setItem(
          'enableSourceSelection',
          JSON.stringify(
            res.data.business?.enableSourceSelection ??
              res.data.enableSourceSelection ??
              true
          )
        );
        localStorage.setItem(
          'enableAmountInput',
          JSON.stringify(res.data.business?.enableAmountInput === true)
        );
        const showGiftVoucherFlow =
          res.data?.showGiftVoucherFlow ?? res.data?.business?.showGiftVoucherFlow;
        localStorage.setItem(
          'showGiftVoucherFlow',
          JSON.stringify(showGiftVoucherFlow === true || showGiftVoucherFlow === 'true')
        );
        localStorage.setItem('merchants', JSON.stringify(res.data.merchants));
        
        // ✅ Use safe country list fallback
        this.saveCountryList(res);
        localStorage.setItem( 'countryList',JSON.stringify(res.data.countryList));
        localStorage.setItem('paymentRestrictionConfiguration',JSON.stringify(res.data.business?.paymentRestrictionConfiguration || {}));
        localStorage.setItem('testPayment', JSON.stringify(res.data.testPayment ?? res.data.business?.testPayment ?? false));

        
        environment.primaryColor = this.themeData.primaryColor;
        this.applyThemeColor(this.themeData.primaryColor);
        environment.logoImage = this.themeData.logo;
        environment.companyName = this.themeData.name;
        environment.entityName = this.themeData.entityName;
        environment.bannerImage = this.themeData.bannerImage;
        environment.paymentType = this.themeData.paymentType;
        environment.status = this.themeData.status;
        environment.googleLink = res.data.googleLink;
        environment.termsAndConditions =
          res.data.termsAndConditions || res.data.business?.termsAndConditions || '';
        environment.privacyPolicy =
          res.data.privacyPolicy || res.data.business?.privacyPolicy || '';
        environment.contactUs = res.data.contactUs || res.data.business?.contactUs || '';

        localStorage.setItem('termsAndConditions', environment.termsAndConditions || '');
        localStorage.setItem('privacyPolicy', environment.privacyPolicy || '');

        const social =
          res.data?.business?.socialMediaUrls ||
          res.data?.socialMediaUrls ||
          {};
        const facebookUrl = (social.facebookUrl || '').trim();
        const instagramUrl = (social.instagramUrl || '').trim();
        const youtubeUrl = (social.youtubeUrl || '').trim();
        const websiteUrl = (social.websiteUrl || '').trim();

        localStorage.setItem(
          'socialMediaUrls',
          JSON.stringify({
            facebookUrl,
            instagramUrl,
            youtubeUrl,
            websiteUrl,
          })
        );
        localStorage.setItem('facebookUrl', facebookUrl);
        localStorage.setItem('instagramUrl', instagramUrl);
        localStorage.setItem('youtubeUrl', youtubeUrl);
        localStorage.setItem('websiteUrl', websiteUrl);
        (environment as any).facebookUrl = facebookUrl;
        (environment as any).instagramUrl = instagramUrl;
        (environment as any).youtubeUrl = youtubeUrl;
        (environment as any).websiteUrl = websiteUrl;
        
        this.screenLoading = false;
        this.businessDisabled = false;
        this.errorMessage = null;
        // After brand is ready, soft-load payment/refer SDKs in idle time
        this.externalScripts.preloadAfterTheme();
        setTimeout(() => this.tryShowInstallDialog(), 800);
      },
      error: (err: any) => {
        this.screenLoading = false;
        switch (err.status) {
          case 404: this.errorMessage = 'Not found (404).'; break;
          case 401: this.errorMessage = 'Unauthorized (401). Please log in again.'; break;
          case 403: this.errorMessage = 'Forbidden (403). You don\'t have permission.'; break;
          case 500: this.errorMessage = 'Server error (500). Please try again later.'; break;
          default: this.errorMessage = 'Something went wrong. Please refresh the page.'; break;
        }
      }
    });
  }

  // ✅ New method to safely store country list with fallback
  saveCountryList(res: any) {
    const defaultCountry = [{
      id: 'default-in',
      countryName: 'India',
      shortName: 'IN',
      callingCode: '+91',
      maxDigits: 10,
      isActive: true,
      currencyCode: 'INR',
      currencySymbol: '₹'
    }];
    const countryList = res?.data?.countryList && res.data.countryList.length > 0
      ? res.data.countryList
      : defaultCountry;
    localStorage.setItem('countryList', JSON.stringify(countryList));
  }

  updateMetaTagsData(res : any) {
    this.titleService.setTitle(res.data.name); 
    this.metaService.updateTag({ property: 'og:title', content: res.data.name });
    this.metaService.updateTag({ property: 'og:image', content: res.data.logo });
  }

  updateFavicon(faviconUrl: string) {
    let link: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/png';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  } 

  containsDigit1To9(value: string): boolean {
    const regex = /[1-9]/;
    return regex.test(value);
  }

  private timeout: any;
  private readonly TIMEOUT_DURATION = 15 * 60 * 1000;
  private readonly LAST_ACTIVITY_KEY = 'lastActivityAt';
  private loggingOutDueToInactivity = false;
  private lastPersistAt = 0;

  @HostListener('document:mousemove')
  @HostListener('document:keydown')
  @HostListener('document:mousedown')
  @HostListener('document:touchstart')
  @HostListener('document:scroll')
  handleUserActivity() {
    if (!this.isUserLoggedIn()) {
      return;
    }
    this.markUserActivity();
    this.resetInactivityTimer();
  }

  /** When app/tab is reopened or becomes visible again — check idle expiry / session. */
  @HostListener('document:visibilitychange')
  @HostListener('window:focus')
  @HostListener('window:pageshow')
  handleAppVisibilityChange() {
    this.session.ensureAuthenticated();
    if (!this.isUserLoggedIn()) {
      return;
    }
    // Do not refresh lastActivity on hide/close — closed time must count toward 15 minutes.
    if (document.visibilityState === 'hidden') {
      return;
    }
    this.checkSessionExpiryOnResume();
  }

  private startSessionWatch() {
    this.session.ensureAuthenticated();
    window.addEventListener('storage', () => this.session.ensureAuthenticated());
    this.sessionCheckInterval = setInterval(() => {
      this.session.ensureAuthenticated();
    }, 1000);
  }

  startInactivityTimer() {
    this.resetInactivityTimer();
  }

  private isUserLoggedIn(): boolean {
    return this.session.hasSession();
  }

  private persistLastActivity(timestamp: number = Date.now()): void {
    localStorage.setItem(this.LAST_ACTIVITY_KEY, String(timestamp));
  }

  private getLastActivityAt(): number | null {
    const raw = localStorage.getItem(this.LAST_ACTIVITY_KEY);
    if (!raw) {
      return null;
    }
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  }

  private markUserActivity(): void {
    const now = Date.now();
    // Avoid writing localStorage on every mousemove; keep stamp fresh enough for close/reopen checks
    if (now - this.lastPersistAt < 1000) {
      return;
    }
    this.lastPersistAt = now;
    this.persistLastActivity(now);
  }

  /** On open/resume: logout if last activity was >= 15 minutes ago. */
  checkSessionExpiryOnResume(): void {
    if (!this.isUserLoggedIn() || this.loggingOutDueToInactivity) {
      return;
    }

    const lastActivity = this.getLastActivityAt();
    if (lastActivity == null) {
      // First run after login / older installs — start the idle window now
      this.markUserActivity();
      this.resetInactivityTimer();
      return;
    }

    const idleFor = Date.now() - lastActivity;
    if (idleFor >= this.TIMEOUT_DURATION) {
      this.logoutDueToInactivity();
      return;
    }

    this.resetInactivityTimer(this.TIMEOUT_DURATION - idleFor);
  }

  resetInactivityTimer(delay: number = this.TIMEOUT_DURATION) {
    if (!this.isUserLoggedIn()) {
      return;
    }
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => this.logoutDueToInactivity(), Math.max(0, delay));
  }

  logoutDueToInactivity() {
    if (this.loggingOutDueToInactivity || !this.isUserLoggedIn()) {
      return;
    }
    this.loggingOutDueToInactivity = true;
    clearTimeout(this.timeout);
    this.snackBar.open('You have been logged out due to inactivity.', 'Close', { duration: 3000 });
    setTimeout(() => {
      this.session.redirectToLogin();
    }, 1500);
  }

  /** Custom install card when the site is open in a browser and not already installed. */
  private tryShowInstallDialog(): void {
    if (this.screenLoading || this.businessDisabled || this.errorMessage || this.showPaymentWait) {
      return;
    }
    this.installPrompt.tryAutoShow();
  }

  /** Apply brand theme from API / localStorage so CSS var(--theme-color) works on any route refresh. */
  private applyThemeColor(color: string | null | undefined): void {
    const themeColor = color || localStorage.getItem('primaryColor') || environment.primaryColor || '#EB1F27';
    environment.primaryColor = themeColor;
    document.documentElement.style.setProperty('--theme-color', themeColor);
  }

  linkClick() {
    const url = new URL(this.data.url);
    const queryParams = new URLSearchParams(url.search);
    const utm_ref_id = queryParams.get('utm_ref_id')
    if (utm_ref_id) {
      this.commonService.linkClicks(utm_ref_id).subscribe({
        next: (res: any) => console.log('Link opened'),
        error: (err: any) => console.error('Link clicked not worked')
      });
    }
  }
}
